import React, { useEffect } from "react";
import type { RefObject } from "react";
import InstructionsPanel from "./InstructionsPanel";
import CheckedIndicator from "./CheckedIndicator";
import screenShareImage from "../assets/screen-share.png";

interface ScreenSharingProps {
  isScreenSharing: boolean;
  screenStream: MediaStream | null;
  stopScreenSharing: () => void;
  handleShareScreen: () => Promise<void> | void;
  videoRef: RefObject<HTMLVideoElement | null>;
  instructions: string[];
  isGuidePreparing?: boolean;
  isGuideReady?: boolean;
  allStepsComplete?: boolean;
  footer?: React.ReactNode;
}

const ScreenSharing: React.FC<ScreenSharingProps> = ({
  isScreenSharing,
  screenStream,
  stopScreenSharing,
  handleShareScreen,
  videoRef,
  instructions,
  isGuidePreparing,
  isGuideReady,
  allStepsComplete,
  footer,
}) => {
  useEffect(() => {
    if (videoRef.current && screenStream && isScreenSharing) {
      videoRef.current.srcObject = screenStream;
      videoRef.current.play().catch((error) => {
        // ignore playback failures
      });
    }
  }, [screenStream, isScreenSharing, videoRef]);

  const handleShareScreenClick = () => {
    handleShareScreen();
  };

  return (
    <div className="w-full bg-[#FAFAFA] gap-8 mx-auto flex flex-col p-6 border-2 border-[#DBDBDB] rounded-xl">
      <div className="flex justify-between gap-[50px]">
        <div className="w-fit min-w-[428px] flex flex-col gap-4 rounded-xl h-[40vh] xl:h-[23vh] 2xl:max-h-[25vh]">
          <div className="flex justify-between items-center">
            <div>
              {isScreenSharing ? (
                <button
                  className="border border-1 border-button bg-white text-button text-center text-[0.86rem] font-normal font-ubuntu leading-[1.24rem] flex py-2 px-3 justify-center items-center rounded-[5px] w-[10vw]"
                  onClick={stopScreenSharing}
                  type="button"
                >
                  Stop Sharing
                </button>
              ) : (
                <button
                  className="text-white bg-button text-center text-[14px] font-normal font-ubuntu leading-[1.24rem] flex py-2 px-3 justify-center items-center rounded-[5px] border border-1 border-button w-[10vw]"
                  onClick={handleShareScreenClick}
                  type="button"
                >
                  Share Screen
                </button>
              )}
            </div>

            {isScreenSharing && <CheckedIndicator />}
          </div>

          <div className="w-[428px] h-full min-h-[240px] max-h-[240px] bg-[#EDEDED] flex p-1 justify-center items-center rounded-md xl:min-h-[25vh]">
            {isScreenSharing && screenStream ? (
              <video
                ref={videoRef}
                className="w-full h-full object-contain xl:min-w-[448px] xl:max-w-[428px]"
                autoPlay
                playsInline
                muted
                style={{ backgroundColor: "transparent" }}
              />
            ) : isScreenSharing ? (
              <div className="text-center text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-button mx-auto mb-2" />
                <p>Loading screen share...</p>
              </div>
            ) : (
              <div className="text-center text-gray-500">
                <p>Click "Share Screen" to start</p>
              </div>
            )}
          </div>
        </div>

        <InstructionsPanel
          instructions={instructions}
          showImage
          imageComponent={
            <img src={screenShareImage} alt="screen_share" className="xl:max-h-[20vh] xl:min-w-[448px] xl:max-w-[428px]" />
          }
        />
      </div>

      {footer && <div className="mt-4 w-full">{footer}</div>}
    </div>
  );
};

export default ScreenSharing;
